import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { MdArrowBack, MdContentCopy, MdSchedule, MdSearch, MdSend } from 'react-icons/md';
import { getData } from '../../../Webservice/Webservice';
import { FONT, PhoneBubble, Progress, SMS_LOG_STATUS, SmsChip, authToken, fillTemplate, fmtDateTime, pct } from './smsShared';

const PAGE_SIZE = 25;

function SmsDetails() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [logStatus, setLogStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (logStatus) params.status = logStatus;
      if (search) params.search = search;
      setData(await getData(`sms-campaign/${campaignId}`, params, authToken(), false));
      setError('');
    } catch (e) {
      if (!quiet) setError('Failed to load campaign details.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [campaignId, page, logStatus, search]);

  useEffect(() => { load(); }, [load]);

  const status = data?.campaign?.status;
  const live = status === 'running' || status === 'pending';
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => load(true), 5000);
    return () => clearInterval(t);
  }, [live, load]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  if (loading && !data) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  if (error || !data?.campaign) return <Box sx={{ p: 3 }}><Button startIcon={<MdArrowBack />} onClick={() => navigate('/sms')} sx={{ textTransform: 'none', fontWeight: 700 }}>Back</Button><Alert severity="error" sx={{ mt: 2 }}>{error || 'Campaign not found'}</Alert></Box>;

  const { campaign, logs, meta } = data;
  const s = campaign.stats || {};
  const message = fillTemplate(campaign.templateId?.content || '', campaign.variableValues || {});
  const cards = [
    { label: 'Recipients', value: s.total || 0, color: '#6366f1', bg: '#eef2ff' },
    { label: 'Sent', value: s.sent || 0, color: '#059669', bg: '#d1fae5' },
    { label: 'Waiting', value: Math.max(0, s.pending || 0), color: '#b45309', bg: '#fef3c7' },
    { label: 'Failed', value: s.failed || 0, color: '#dc2626', bg: '#fee2e2' },
    { label: 'Success rate', value: s.total ? `${pct(s.sent || 0, (s.sent || 0) + (s.failed || 0) || 1)}%` : '-', color: '#0369a1', bg: '#e0f2fe' },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1250, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <IconButton onClick={() => navigate('/sms')} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}><MdArrowBack size={20} /></IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 23.5, color: '#0f172a', lineHeight: 1.2 }}>{campaign.name}</Typography>
                <SmsChip status={status} />
                {live && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#059669', fontFamily: FONT, fontSize: 11.5, fontWeight: 700 }}><CircularProgress size={12} color="inherit" /> live</Box>}
              </Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{campaign.campId ? `${campaign.campId} · ` : ''}{campaign.templateId?.name || 'Template'}</Typography>
            </Box>
          </Box>
          <Button onClick={() => navigate('/sms', { state: { duplicateId: campaignId } })} startIcon={<MdContentCopy />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#e0f2fe', color: '#075985', '&:hover': { background: '#bae6fd' } }}>Duplicate</Button>
        </Box>

        {status === 'scheduled' && <Alert severity="info" icon={<MdSchedule />} sx={{ mb: 2 }}>Scheduled for <b>{fmtDateTime(campaign.scheduledAt)}</b>. It goes out by itself at that time.</Alert>}

        <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
          <Progress stats={{ ...s, pending: Math.max(0, s.pending || 0) }} status={status} height={12} />
          <Box sx={{ mt: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {[['Created', fmtDateTime(campaign.createdAt)], ['Scheduled for', campaign.scheduledAt ? fmtDateTime(campaign.scheduledAt) : 'Sent immediately'], ['Last update', fmtDateTime(campaign.updatedAt)]].map(([k, v]) => (
              <Box key={k}>
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>{v}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1.5, mb: 2.5 }}>
          {cards.map((c) => (
            <Paper key={c.label} elevation={0} sx={{ p: 2, borderRadius: '14px', background: c.bg }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700, color: c.color, opacity: 0.85 }}>{c.label}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 21.5, fontWeight: 800, color: c.color }}>{c.value}</Typography>
            </Paper>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '360px 1fr' }, gap: 2.5, alignItems: 'start' }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1.5 }}>Message sent</Typography>
            <PhoneBubble text={message} empty="This template has no preview text." />
            {campaign.variableValues && Object.keys(campaign.variableValues).length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                {Object.entries(campaign.variableValues).map(([k, v]) => (
                  <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: 11.5, color: '#64748b' }}>{k}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700 }}>{v}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5 }}><MdSend style={{ verticalAlign: 'middle', marginRight: 6 }} />Delivery log</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField size="small" placeholder="Search mobile" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} InputProps={{ startAdornment: <MdSearch size={18} style={{ marginRight: 6, color: '#94a3b8' }} /> }} />
                <TextField select size="small" label="Status" value={logStatus} onChange={(e) => { setLogStatus(e.target.value); setPage(1); }} sx={{ minWidth: 130 }}>
                  <MenuItem value="">All</MenuItem>
                  {Object.entries(SMS_LOG_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </TextField>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 800, py: 1.25, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', fontFamily: FONT } }}>
                    <TableCell>#</TableCell><TableCell>Customer</TableCell><TableCell>Mobile</TableCell><TableCell>Status</TableCell><TableCell>Sent</TableCell><TableCell>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: '#64748b', fontFamily: FONT }}>No entries.</TableCell></TableRow>
                  ) : logs.map((l, i) => (
                    <TableRow key={l._id} hover sx={{ '& td': { fontFamily: FONT, fontSize: 12 } }}>
                      <TableCell>{(meta.page - 1) * meta.limit + i + 1}</TableCell>
                      <TableCell>{l.customerId?.name || 'N/A'}</TableCell>
                      <TableCell>{l.mobile}</TableCell>
                      <TableCell><SmsChip status={l.status} map={SMS_LOG_STATUS} /></TableCell>
                      <TableCell>{l.sentAt ? fmtDateTime(l.sentAt) : '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}><Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: l.status === 'failed' ? '#b91c1c' : '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.error}>{l.error || ''}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {meta.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <Pagination count={meta.totalPages} page={meta.page} shape="rounded" showFirstButton showLastButton onChange={(_, p) => setPage(p)} sx={{ '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black' } }} />
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

export default SmsDetails;
